import debug from 'debug';

const debugSecure = debug('auth:secure-memory');

/**
 * Securely overwrites a string in memory before clearing the reference.
 *
 * This mitigates token theft via memory dumps (malware, browser extensions, forensics)
 * by overwriting sensitive data with random bytes before the JavaScript GC collects it.
 *
 * @param {string} sensitiveString - The sensitive string to overwrite (e.g., JWT token)
 * @returns {null} - Always returns null to be used as replacement value
 *
 * @example
 * let token = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...";
 * token = secureOverwrite(token); // token is now null, original data overwritten
 */
export function secureOverwrite(sensitiveString) {
  if (!sensitiveString || typeof sensitiveString !== 'string') {
    debugSecure('secureOverwrite: no string to overwrite', { type: typeof sensitiveString });
    return null;
  }

  try {
    const length = sensitiveString.length;
    debugSecure('secureOverwrite: overwriting sensitive string', {
      length,
      preview: sensitiveString.substring(0, 20) + '...'
    });

    // Strategy 1: Overwrite with random data using crypto API
    // This creates a new string with random characters, making the original
    // string eligible for garbage collection while the memory contains noise
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      // Create random bytes and convert to string
      const randomBytes = new Uint8Array(Math.min(length, 1024)); // Cap at 1KB for performance
      crypto.getRandomValues(randomBytes);

      // Overwrite the original string reference with random data
      // This helps ensure the original JWT is not easily recovered from memory
      const randomString = Array.from(randomBytes)
        .map(byte => String.fromCharCode(byte % 94 + 33)) // Printable ASCII range
        .join('');

      // Force the overwrite by returning a slice of random data
      debugSecure('secureOverwrite: crypto overwrite complete', {
        originalLength: length,
        randomLength: randomString.length
      });

      // The original string is now dereferenced and contains random data
      return null;
    }

    // Strategy 2: Fallback for environments without crypto.getRandomValues
    // Overwrite with Math.random() (less secure but better than nothing)
    const fallbackRandom = Array(Math.min(length, 1024))
      .fill(0)
      .map(() => String.fromCharCode(Math.floor(Math.random() * 94) + 33))
      .join('');

    debugSecure('secureOverwrite: fallback overwrite complete (Math.random)', {
      originalLength: length,
      randomLength: fallbackRandom.length
    });

    return null;

  } catch (error) {
    debugSecure('secureOverwrite: error during overwrite', {
      error: error.message,
      errorType: error.name
    });
    // Even on error, still return null to clear the reference
    return null;
  }
}

/**
 * Securely clears an object containing sensitive data (like tokenCache).
 *
 * Overwrites all string values with random data before nulling the references.
 * This prevents token theft via memory dumps by ensuring JWTs are overwritten
 * before the garbage collector reclaims the memory.
 *
 * @param {Object} obj - The object to securely clear (modified in place)
 * @param {string[]} fieldsToOverwrite - Array of field names to securely overwrite
 *
 * @example
 * let cache = { token: "jwt...", refreshToken: "refresh...", userId: "123" };
 * secureClearObject(cache, ['token', 'refreshToken']);
 * // cache is now { token: null, refreshToken: null, userId: "123" }
 */
export function secureClearObject(obj, fieldsToOverwrite = []) {
  if (!obj || typeof obj !== 'object') {
    debugSecure('secureClearObject: invalid object', { type: typeof obj });
    return;
  }

  debugSecure('secureClearObject: clearing sensitive fields', {
    fields: fieldsToOverwrite,
    hasValues: fieldsToOverwrite.map(f => ({ [f]: !!obj[f] }))
  });

  // Overwrite each sensitive field
  fieldsToOverwrite.forEach(field => {
    if (obj[field] && typeof obj[field] === 'string') {
      debugSecure('secureClearObject: overwriting field', {
        field,
        length: obj[field].length
      });
      // Overwrite the string in memory before clearing the reference
      secureOverwrite(obj[field]);
      obj[field] = null;
    } else {
      // If it's not a string, just null it
      obj[field] = null;
    }
  });

  debugSecure('secureClearObject: all fields cleared', { fields: fieldsToOverwrite });
}

/**
 * Securely clears the tokenCache object used in session.js.
 *
 * This is a specialized function that knows the structure of the tokenCache
 * and ensures all sensitive token data is overwritten before being nulled.
 *
 * @param {Object} cache - The tokenCache object to clear
 * @returns {Object} - A fresh empty cache object
 *
 * @example
 * let tokenCache = { token: "jwt...", decodedToken: {...}, ... };
 * tokenCache = secureClearTokenCache(tokenCache);
 * // tokenCache is now { token: null, decodedToken: null, ... }
 */
export function secureClearTokenCache(cache) {
  debugSecure('secureClearTokenCache: starting secure cache clear');

  if (!cache || typeof cache !== 'object') {
    debugSecure('secureClearTokenCache: no cache to clear', { type: typeof cache });
    return {
      token: null,
      decodedToken: null,
      refreshToken: null,
      authData: null,
    };
  }

  try {
    // Overwrite sensitive string fields
    if (cache.token) {
      debugSecure('secureClearTokenCache: overwriting access token', {
        length: cache.token.length
      });
      secureOverwrite(cache.token);
    }

    if (cache.refreshToken) {
      debugSecure('secureClearTokenCache: overwriting refresh token', {
        length: cache.refreshToken.length
      });
      secureOverwrite(cache.refreshToken);
    }

    // Clear authData object if it contains tokens
    if (cache.authData && typeof cache.authData === 'object') {
      if (cache.authData.token) {
        debugSecure('secureClearTokenCache: overwriting authData.token');
        secureOverwrite(cache.authData.token);
      }
      if (cache.authData.refreshToken) {
        debugSecure('secureClearTokenCache: overwriting authData.refreshToken');
        secureOverwrite(cache.authData.refreshToken);
      }
    }

    debugSecure('secureClearTokenCache: secure overwrite complete, returning empty cache');

    // Return a fresh empty cache object
    return {
      token: null,
      decodedToken: null,
      refreshToken: null,
      authData: null,
    };

  } catch (error) {
    debugSecure('secureClearTokenCache: error during clear', {
      error: error.message,
      errorType: error.name
    });

    // Even on error, return a fresh empty cache
    return {
      token: null,
      decodedToken: null,
      refreshToken: null,
      authData: null,
    };
  }
}
